import { Component, OnInit, ViewChild, ElementRef, Input, Output, EventEmitter, TemplateRef } from '@angular/core';
import { DragHandle } from '../../../../../shared/model/time-range.model';
import { ControlContainer, FormControl, FormGroup, NgForm, Validators } from '@angular/forms';
import { Group, Interval, Product } from '../../../../../shared/model/product.model';
import { ColorService } from '../../../../../shared/services/color.service';
import { Locale } from '../../../../../shared/model/locale.model';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-time-range-slider',
  templateUrl: './time-range-slider.component.html',
  styleUrls: ['./time-range-slider.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: NgForm }],
})
export class TimeRangeSliderComponent implements OnInit {
  @Input() mode: string;
  @Input() locale: Locale;
  @Input() frm: FormGroup;

  @Input() offerGroups!: Group[] | null;
  @Output() groupsChanged = new EventEmitter<Group[]>();
  @Output() saveRequested = new EventEmitter<Group[]>();

  @ViewChild('sliderTrack', { static: false }) sliderTrack!: ElementRef;
  @ViewChild('noSpaceModal') noSpaceModal!: TemplateRef<any>;

  groups: Group[] = [];
  showAddGroupModal = false;
  newGroupName = new FormControl('', [Validators.required]);
  isAvailableSlot: boolean = true;

  private readonly MINUTES_IN_DAY = 1440;
  private readonly MIN_STEP = 15;
  private readonly DEFAULT_INTERVAL_DURATION = 60;
  private readonly INITIAL_INTERVAL_DURATION = 60;

  private currentDrag: DragHandle | null = null;
  private dragStartX = 0;

  // State to track availability
  private availabilityState = {
    isAvailable: true,
    lastCheckedSlot: -1,
  };

  // Check and update availability state
  private checkAndUpdateAvailability(duration: number = this.DEFAULT_INTERVAL_DURATION): boolean {
    const availableSlot = this.findAvailableSlot(duration);
    this.availabilityState = {
      isAvailable: availableSlot !== -1,
      lastCheckedSlot: availableSlot,
    };
    this.isAvailableSlot = this.availabilityState.isAvailable;
    return this.availabilityState.isAvailable;
  }

  // Find available slot for new interval
  private findAvailableSlot(duration: number): number {
    const allIntervals = this.getAllIntervals();

    for (let start = 0; start <= this.MINUTES_IN_DAY - duration; start += this.MIN_STEP) {
      const end = start + duration;
      const hasOverlap = allIntervals.some((interval) => this.checkOverlap(start, end, interval.start, interval.end));

      if (!hasOverlap) {
        return start;
      }
    }
    return -1;
  }

  constructor(private colorService: ColorService, private dialog: MatDialog) {}

  ngOnInit(): void {
    if (this.offerGroups.length > 0) {
      console.log('Initializing with offer groups:', this.offerGroups);
      this.groups = [...this.offerGroups];
    }
  }

  private notifyChanges(): void {
    const groupsCopy = this.groups.map((group) => ({
      id: this.generateGroupId(),
      groupName: group.groupName,
      intervals: group.intervals.map((interval) => ({
        id: this.generateIntervalId(),
        startTm: interval.startTm,
        endTm: interval.endTm,
      })),
    }));
    this.groupsChanged.emit(groupsCopy);
  }

  // Hour labels for the timeline
  get hours(): number[] {
    return Array.from({ length: 13 }, (_, i) => i * 2);
  }

  // Open add group modal
  openAddGroupModal(): void {
    if (!this.checkAndUpdateAvailability(this.INITIAL_INTERVAL_DURATION)) {
      this.dialog.open(this.noSpaceModal, {
        width: '400px',
      });
      return;
    }
    this.showAddGroupModal = true;

    this.newGroupName = new FormControl('', [Validators.required]);
  }

  // Close add group modal
  closeAddGroupModal(): void {
    this.showAddGroupModal = false;
    this.newGroupName.reset();
  }

  // Add new group
  addGroup(): void {
    if (!this.newGroupName.valid || !this.checkAndUpdateAvailability(this.INITIAL_INTERVAL_DURATION)) {
      return;
    }

    const startMinutes = this.availabilityState.lastCheckedSlot;

    if (startMinutes === -1) {
      this.dialog.open(this.noSpaceModal, {
        width: '400px',
      });
      return;
    }

    const newGroup: Group = {
      id: this.generateGroupId(),
      groupName: this.newGroupName.value,
      color: this.colorService.getNextColor(this.groups.length),
      intervals: [],
    };

    const newInterval: Interval = {
      id: this.generateIntervalId(),
      start: startMinutes,
      end: startMinutes + this.INITIAL_INTERVAL_DURATION,
      startTm: this.minutesToTime(startMinutes),
      endTm: this.minutesToTime(startMinutes + this.INITIAL_INTERVAL_DURATION),
      groupId: newGroup.id,
      isNew: true,
    };

    newGroup.intervals.push(newInterval);
    this.groups.push(newGroup);
    this.notifyChanges();
    this.closeAddGroupModal();
  }

  // Add interval to existing group
  addInterval(group: Group): void {
    if (!this.checkAndUpdateAvailability()) {
      this.dialog.open(this.noSpaceModal, {
        width: '400px',
      });
      return;
    }

    const startMinutes = this.availabilityState.lastCheckedSlot;

    if (startMinutes === -1) {
      this.isAvailableSlot = false;
      return;
    } else {
      this.isAvailableSlot = true;
    }

    const newInterval: Interval = {
      id: this.generateIntervalId(),
      start: startMinutes,
      end: startMinutes + this.DEFAULT_INTERVAL_DURATION,
      startTm: this.minutesToTime(startMinutes),
      endTm: this.minutesToTime(startMinutes + this.DEFAULT_INTERVAL_DURATION),
      groupId: group.id,
      isNew: true,
    };

    group.intervals.push(newInterval);
    this.notifyChanges();
  }

  // Remove entire group
  removeGroup(groupId: string): void {
    console.log('Removing group with ID:', groupId);
    this.groups = this.groups.filter((g) => g.id !== groupId);
    this.checkAndUpdateAvailability();
    this.notifyChanges();
  }

  // Remove single interval
  removeInterval(group: Group, intervalId: string): void {
    console.log('Removing interval with ID:', intervalId, 'from group:', group);
    group.intervals = group.intervals.filter((i) => i.id !== intervalId);
    this.checkAndUpdateAvailability();
    this.notifyChanges();
  }

  // Check if two time ranges overlap
  private checkOverlap(start1: number, end1: number, start2: number, end2: number): boolean {
    return start1 < end2 && end1 > start2;
  }

  // Get all intervals from all groups
  private getAllIntervals(): Interval[] {
    return this.groups.flatMap((g) => g.intervals);
  }

  // Start dragging handle
  onHandleMouseDown(event: MouseEvent, interval: Interval, type: 'start' | 'end'): void {
    event.preventDefault();
    this.currentDrag = {
      intervalId: interval.id,
      type: type,
      initialPosition: type === 'start' ? interval.start : interval.end,
    };
    this.dragStartX = event.clientX;

    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
    this.checkAndUpdateAvailability();
    this.notifyChanges();
  }

  // Handle mouse move during drag
  private onMouseMove = (event: MouseEvent): void => {
    if (!this.currentDrag || !this.sliderTrack) return;

    const track = this.sliderTrack.nativeElement;
    const trackWidth = track.offsetWidth;
    const deltaX = event.clientX - this.dragStartX;
    const deltaMinutes = Math.round(((deltaX / trackWidth) * this.MINUTES_IN_DAY) / this.MIN_STEP) * this.MIN_STEP;

    const newPosition = Math.max(0, Math.min(this.MINUTES_IN_DAY, this.currentDrag.initialPosition + deltaMinutes));

    const interval = this.findIntervalById(this.currentDrag.intervalId);
    if (!interval) return;

    let newStart = interval.start;
    let newEnd = interval.end;

    if (this.currentDrag.type === 'start') {
      newStart = Math.min(newPosition, interval.end - this.MIN_STEP);
    } else {
      newEnd = Math.max(newPosition, interval.start + this.MIN_STEP);
    }

    // Check for overlaps with other intervals
    if (!this.wouldOverlap(interval.id, newStart, newEnd)) {
      interval.start = newStart;
      interval.end = newEnd;
      this.checkAndUpdateAvailability();
      this.notifyChanges();
    }
    this.checkAndUpdateAvailability();
    this.notifyChanges();
  };

  // Handle mouse up to stop dragging
  private onMouseUp = (): void => {
    if (this.currentDrag) {
      const interval = this.findIntervalById(this.currentDrag.intervalId);
      if (interval) {
        interval.startTm = this.minutesToTime(interval.start);
        interval.endTm = this.minutesToTime(interval.end);
      }
    }

    this.currentDrag = null;
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
    this.checkAndUpdateAvailability();
    this.notifyChanges();
  };

  // Check if new position would cause overlap
  private wouldOverlap(currentIntervalId: string, newStart: number, newEnd: number): boolean {
    return this.getAllIntervals()
      .filter((i) => i.id !== currentIntervalId)
      .some((i) => this.checkOverlap(newStart, newEnd, i.start, i.end));
  }

  // Find interval by ID
  private findIntervalById(id: string): Interval | undefined {
    for (const group of this.groups) {
      const interval = group.intervals.find((i) => i.id === id);
      if (interval) return interval;
    }
    return undefined;
  }

  private generateGroupId(): string {
    return `group-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateIntervalId(): string {
    return `interval-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // Convert minutes to time string
  minutesToTime(minutes: number): string {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
  }

  // Calculate position percentage
  getPositionPercent(minutes: number): number {
    return (minutes / this.MINUTES_IN_DAY) * 100;
  }

  // Calculate width percentage
  getWidthPercent(start: number, end: number): number {
    return ((end - start) / this.MINUTES_IN_DAY) * 100;
  }

  get isEditMode() {
    return this.mode === 'edit';
  }

  get isCreateMode() {
    return this.mode === 'create';
  }

  get isViewMode() {
    return this.mode === 'view';
  }

  getContrastColor(bgColor: string): string {
    // Remove the '#' if present
    const hex = bgColor.replace('#', '');

    // Convert hex to RGB
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Calculate relative luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Return black for light backgrounds, white for dark backgrounds (lower threshold for dark palettes (0.35)
    return luminance > 0.35 ? '#000000' : '#FFFFFF';
  }
}
